import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogContent, Box, Typography, IconButton, Chip,
  TextField, MenuItem, Button, CircularProgress, Paper,
  Snackbar, Alert, FormControl, InputLabel, Select,
  Collapse, Tooltip, Popover,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import HealingRoundedIcon from '@mui/icons-material/HealingRounded';
import MedicalServicesRoundedIcon from '@mui/icons-material/MedicalServicesRounded';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import ChildCareRoundedIcon from '@mui/icons-material/ChildCareRounded';
import RemoveRedEyeRoundedIcon from '@mui/icons-material/RemoveRedEyeRounded';
import AccessibilityNewRoundedIcon from '@mui/icons-material/AccessibilityNewRounded';
import PetsRoundedIcon from '@mui/icons-material/PetsRounded';
import LocalPharmacyRoundedIcon from '@mui/icons-material/LocalPharmacyRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const API_URL = '/api';

const ICON_MAP = {
  AutoAwesomeRounded: <AutoAwesomeRoundedIcon />,
  HealingRounded: <HealingRoundedIcon />,
  MedicalServicesRounded: <MedicalServicesRoundedIcon />,
  FavoriteRounded: <FavoriteRoundedIcon />,
  ChildCareRounded: <ChildCareRoundedIcon />,
  RemoveRedEyeRounded: <RemoveRedEyeRoundedIcon />,
  AccessibilityNewRounded: <AccessibilityNewRoundedIcon />,
  PetsRounded: <PetsRoundedIcon />,
  LocalPharmacyRounded: <LocalPharmacyRoundedIcon />,
};

const CATEGORIAS = [
  { value: 'todas',      label: 'Todas' },
  { value: 'medicina',   label: 'Medicina general' },
  { value: 'estetica',   label: 'Estética' },
  { value: 'odontologia', label: 'Odontología' },
  { value: 'pediatria',  label: 'Pediatría' },
  { value: 'veterinaria', label: 'Veterinaria' },
  { value: 'otra',       label: 'Otra' },
];

function getHeaders() {
  const userId = localStorage.getItem('user_id');
  return userId
    ? { 'X-User-Id': String(userId), 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' };
}

export default function PlantillasGaleria({ open, onClose, chatbots = [], onCloned }) {
  const [plantillas, setPlantillas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categoria, setCategoria] = useState('todas');
  const [busqueda, setBusqueda] = useState('');
  const [snack, setSnack] = useState(null);

  // Popover de detalle (icono i en card)
  const [detailAnchor, setDetailAnchor] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState(null);

  // Diálogo de uso (crear nuevo o aplicar a existente)
  const [useDialog, setUseDialog] = useState(false);
  const [selected, setSelected] = useState(null);
  const [modo, setModo] = useState('nuevo'); // 'nuevo' | 'existente'
  const [nombreNuevo, setNombreNuevo] = useState('');
  const [chatbotIdExist, setChatbotIdExist] = useState('');
  const [sobrescribirKb, setSobrescribirKb] = useState(false);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetch(`${API_URL}/smart-flow-templates`, { headers: getHeaders() })
      .then((r) => r.json())
      .then((data) => setPlantillas(Array.isArray(data) ? data : []))
      .catch(() => setPlantillas([]))
      .finally(() => setLoading(false));
  }, [open]);

  const filtradas = useMemo(() => {
    let arr = plantillas;
    if (categoria !== 'todas') arr = arr.filter((p) => p.categoria === categoria);
    if (busqueda.trim()) {
      const q = busqueda.trim().toLowerCase();
      arr = arr.filter((p) =>
        p.nombre.toLowerCase().includes(q) ||
        (p.descripcion || '').toLowerCase().includes(q) ||
        (p.especialidad || '').toLowerCase().includes(q)
      );
    }
    return arr;
  }, [plantillas, categoria, busqueda]);

  const handleOpenDetail = async (event, plantilla) => {
    event.stopPropagation();
    setDetailAnchor({ el: event.currentTarget, plantilla });
    setDetailLoading(true);
    setDetailData(null);
    try {
      const r = await fetch(`${API_URL}/smart-flow-templates/${plantilla.id}`, { headers: getHeaders() });
      if (r.ok) setDetailData(await r.json());
    } catch (_) {} finally {
      setDetailLoading(false);
    }
  };

  const handleUsar = (plantilla) => {
    setSelected(plantilla);
    // Sugerencia de nombre: especialidad o nombre de plantilla
    setNombreNuevo(plantilla.especialidad
      ? `Chatbot de ${plantilla.especialidad}`
      : plantilla.nombre.split('—')[0].trim()
    );
    setModo('nuevo');
    setChatbotIdExist('');
    setSobrescribirKb(false);
    setUseDialog(true);
  };

  const handleCrear = async () => {
    if (!selected) return;
    setWorking(true);
    try {
      if (modo === 'nuevo') {
        if (!nombreNuevo.trim()) {
          setSnack({ severity: 'warning', msg: 'El nombre es obligatorio' });
          setWorking(false);
          return;
        }
        const r = await fetch(`${API_URL}/smart-flow-templates/${selected.id}/create-chatbot`, {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify({ nombre: nombreNuevo.trim() }),
        });
        if (!r.ok) throw new Error(await r.text());
        const data = await r.json();
        setUseDialog(false);
        setSelected(null);
        onClose?.();
        setSnack({
          severity: 'success',
          msg: `Chatbot "${data.chatbot.name}" creado · ${data.intents_clonados} intents · ${data.kb_entries_creadas} entradas KB`,
        });
        onCloned?.(data.chatbot.id, data);
      } else {
        if (!chatbotIdExist) {
          setSnack({ severity: 'warning', msg: 'Selecciona un chatbot' });
          setWorking(false);
          return;
        }
        const r = await fetch(`${API_URL}/smart-flow-templates/${selected.id}/clone`, {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify({
            chatbot_id: parseInt(chatbotIdExist, 10),
            sobrescribir_kb: sobrescribirKb,
          }),
        });
        if (!r.ok) throw new Error(await r.text());
        const data = await r.json();
        setUseDialog(false);
        setSelected(null);
        onClose?.();
        setSnack({
          severity: 'success',
          msg: `Plantilla aplicada · ${data.intents_clonados} intents · ${data.kb_entries_creadas} entradas KB`,
        });
        onCloned?.(parseInt(chatbotIdExist, 10), data);
      }
    } catch (e) {
      setSnack({ severity: 'error', msg: 'No se pudo aplicar la plantilla' });
    } finally {
      setWorking(false);
    }
  };

  const iconOf = (icono, color, size = 40) => (
    <Box sx={{
      width: size, height: size, borderRadius: 2.2,
      background: `linear-gradient(135deg, ${color || '#6366f1'}, ${color || '#6366f1'}cc)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff',
      boxShadow: `0 3px 10px ${color || '#6366f1'}35`,
      flexShrink: 0,
      '& svg': { fontSize: Math.round(size * 0.5) },
    }}>
      {ICON_MAP[icono] || <AutoAwesomeRoundedIcon />}
    </Box>
  );

  return (
    <>
      <Dialog
        open={open} onClose={onClose}
        fullWidth maxWidth="sm"
        PaperProps={{ sx: {
          borderRadius: 3, overflow: 'hidden',
          maxHeight: '88vh',
          boxShadow: '0 20px 60px rgba(15, 23, 42, 0.20)',
        } }}
      >
        {/* Header limpio */}
        <Box sx={{
          px: 2.5, py: 1.75,
          display: 'flex', alignItems: 'center', gap: 1.5,
          borderBottom: '1px solid #e2e8f0',
          background: '#fff',
        }}>
          <Box sx={{
            width: 30, height: 30, borderRadius: 1.5,
            background: 'linear-gradient(135deg, #a855f7, #6366f1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff',
          }}>
            <MenuBookRoundedIcon sx={{ fontSize: 16 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#0f172a', lineHeight: 1.2 }}>
              Galería de plantillas
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: '#94a3b8', fontWeight: 500 }}>
              Empieza desde una plantilla curada por NARA Tech
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ color: '#94a3b8' }}>
            <CloseRoundedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        {/* Toolbar */}
        <Box sx={{
          px: 2.5, py: 1.5, display: 'flex', gap: 1,
          borderBottom: '1px solid #e2e8f0',
          flexWrap: 'wrap',
          background: '#fafbfc',
        }}>
          <TextField
            size="small" placeholder="Buscar plantilla…" value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            sx={{
              flex: 1, minWidth: 180,
              '& .MuiInputBase-input': { fontSize: 13, py: 0.85 },
              '& .MuiOutlinedInput-root': { borderRadius: 2, background: '#fff' },
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={categoria} onChange={(e) => setCategoria(e.target.value)}
              sx={{
                fontSize: 13, borderRadius: 2, background: '#fff',
                '& .MuiSelect-select': { py: 0.9 },
              }}
            >
              {CATEGORIAS.map((c) => (
                <MenuItem key={c.value} value={c.value} sx={{ fontSize: 13 }}>{c.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Lista */}
        <DialogContent sx={{ p: 2, background: '#fafbfc' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
              <CircularProgress size={28} />
            </Box>
          ) : filtradas.length === 0 ? (
            <Box sx={{ py: 5, textAlign: 'center' }}>
              <MenuBookRoundedIcon sx={{ fontSize: 36, color: '#cbd5e1', mb: 1 }}/>
              <Typography sx={{ fontWeight: 600, fontSize: 13.5, color: '#0f172a' }}>
                {busqueda ? 'Sin resultados' : 'No hay plantillas publicadas'}
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#94a3b8', mt: 0.25 }}>
                {busqueda
                  ? 'Prueba con otro término o categoría'
                  : 'NARA Tech está preparando plantillas'}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {filtradas.map((p) => (
                <Paper key={p.id} elevation={0} sx={{
                  borderRadius: 2.5,
                  background: '#fff',
                  border: '1px solid #e2e8f0',
                  overflow: 'hidden',
                  transition: 'all 0.18s',
                  '&:hover': {
                    borderColor: p.color || '#6366f1',
                    boxShadow: `0 4px 14px ${(p.color || '#6366f1')}20`,
                  },
                }}>
                  <Box sx={{ p: 1.75, display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    {iconOf(p.icono, p.color, 40)}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {/* Título + premium */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.4 }}>
                        <Typography sx={{
                          fontWeight: 700, fontSize: 13.5, color: '#0f172a',
                          letterSpacing: -0.1, lineHeight: 1.3,
                          flex: 1, minWidth: 0,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {p.nombre}
                        </Typography>
                        {p.is_premium && (
                          <Tooltip title="Premium">
                            <StarRoundedIcon sx={{ fontSize: 14, color: '#f59e0b' }}/>
                          </Tooltip>
                        )}
                      </Box>
                      {/* Descripción 2 líneas */}
                      <Typography sx={{
                        fontSize: 12, color: '#64748b', lineHeight: 1.45, mb: 0.85,
                        overflow: 'hidden', textOverflow: 'ellipsis',
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                      }}>
                        {p.descripcion || '—'}
                      </Typography>
                      {/* Stats inline */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        {p.especialidad && (
                          <Box sx={{
                            px: 0.85, py: 0.15, borderRadius: 1,
                            background: (p.color || '#6366f1') + '15',
                            display: 'flex', alignItems: 'center', gap: 0.4,
                          }}>
                            <Typography sx={{
                              fontSize: 10, fontWeight: 800,
                              color: p.color || '#6366f1',
                              letterSpacing: 0.4, textTransform: 'uppercase',
                            }}>
                              {p.especialidad}
                            </Typography>
                          </Box>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <BoltRoundedIcon sx={{ fontSize: 12, color: '#6366f1' }}/>
                          <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#475569' }}>
                            {p.intents_count}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <MenuBookRoundedIcon sx={{ fontSize: 12, color: '#0ea5e9' }}/>
                          <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#475569' }}>
                            {p.kb_count}
                          </Typography>
                        </Box>
                        {p.veces_usada > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                            <CheckCircleRoundedIcon sx={{ fontSize: 12, color: '#10b981' }}/>
                            <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#475569' }}>
                              {p.veces_usada}
                            </Typography>
                          </Box>
                        )}
                        <Box sx={{ flex: 1 }} />
                        <Tooltip title="Ver detalle">
                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenDetail(e, p)}
                            sx={{
                              p: 0.4, color: '#94a3b8',
                              '&:hover': { color: p.color || '#6366f1', background: (p.color || '#6366f1') + '12' },
                            }}
                          >
                            <InfoOutlinedIcon sx={{ fontSize: 17 }}/>
                          </IconButton>
                        </Tooltip>
                        <Button
                          size="small" onClick={() => handleUsar(p)}
                          endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 14 }}/>}
                          sx={{
                            textTransform: 'none', fontWeight: 700, fontSize: 12,
                            borderRadius: 1.5, px: 1.5, py: 0.4,
                            color: '#fff',
                            background: `linear-gradient(135deg, ${p.color || '#6366f1'}, ${p.color || '#6366f1'}d0)`,
                            boxShadow: `0 2px 8px ${(p.color || '#6366f1')}40`,
                            '&:hover': {
                              background: `linear-gradient(135deg, ${p.color || '#6366f1'}, ${p.color || '#6366f1'})`,
                              boxShadow: `0 4px 12px ${(p.color || '#6366f1')}50`,
                            },
                          }}
                        >
                          Usar
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Diálogo "Usar plantilla" — default: NUEVO chatbot */}
      <Dialog
        open={useDialog}
        onClose={() => !working && setUseDialog(false)}
        fullWidth maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <Box sx={{
          px: 2.5, py: 1.75,
          display: 'flex', alignItems: 'center', gap: 1.5,
          borderBottom: '1px solid #e2e8f0',
        }}>
          {selected && iconOf(selected.icono, selected.color, 32)}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 13.5, color: '#0f172a' }}>
              Usar plantilla
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: '#64748b',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {selected?.nombre}
            </Typography>
          </Box>
        </Box>

        <DialogContent sx={{ p: 2.5 }}>
          {/* Toggle modo */}
          <Box sx={{
            display: 'flex', gap: 0.5, p: 0.5,
            background: '#f1f5f9', borderRadius: 2, mb: 2.25,
          }}>
            <ModeBtn
              label="Nuevo chatbot"
              icon={<AddCircleOutlineRoundedIcon sx={{ fontSize: 15 }}/>}
              active={modo === 'nuevo'}
              onClick={() => setModo('nuevo')}
              color={selected?.color || '#6366f1'}
            />
            <ModeBtn
              label="A uno existente"
              icon={<ArrowForwardRoundedIcon sx={{ fontSize: 15 }}/>}
              active={modo === 'existente'}
              onClick={() => setModo('existente')}
              color={selected?.color || '#6366f1'}
              disabled={chatbots.length === 0}
            />
          </Box>

          {/* MODO NUEVO */}
          <Collapse in={modo === 'nuevo'} unmountOnExit>
            <Box>
              <Typography sx={{ fontSize: 12, color: '#64748b', mb: 1.25, lineHeight: 1.45 }}>
                Se creará un chatbot nuevo con esta plantilla aplicada (system prompt, {selected?.intents_count || 0} intents y {selected?.kb_count || 0} entradas KB). Después podrás conectarle un número de WhatsApp y un plan.
              </Typography>
              <TextField
                autoFocus fullWidth size="small"
                label="Nombre del chatbot"
                value={nombreNuevo}
                onChange={(e) => setNombreNuevo(e.target.value)}
                placeholder="Ej: Asistente Clínica ABC"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Box>
          </Collapse>

          {/* MODO EXISTENTE */}
          <Collapse in={modo === 'existente'} unmountOnExit>
            <Box>
              <Typography sx={{ fontSize: 12, color: '#64748b', mb: 1.25, lineHeight: 1.45 }}>
                Se reemplazará el contenido de Smart Flow del chatbot que elijas. La base de conocimiento se agrega encima (a menos que marques la opción).
              </Typography>
              <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                <InputLabel>Chatbot destino</InputLabel>
                <Select
                  label="Chatbot destino"
                  value={chatbotIdExist}
                  onChange={(e) => setChatbotIdExist(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  {chatbots.length === 0 ? (
                    <MenuItem disabled value="">No tienes chatbots</MenuItem>
                  ) : (
                    chatbots.map((cb) => (
                      <MenuItem key={cb.id} value={String(cb.id)}>{cb.name}</MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
              <Box
                onClick={() => setSobrescribirKb(!sobrescribirKb)}
                sx={{
                  p: 1.25, borderRadius: 2,
                  background: sobrescribirKb ? '#fef3c7' : '#f8fafc',
                  border: `1px solid ${sobrescribirKb ? '#fcd34d' : '#e2e8f0'}`,
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'flex-start', gap: 1,
                  transition: 'all 0.15s',
                }}>
                <Box sx={{
                  width: 16, height: 16, borderRadius: 0.5,
                  border: `2px solid ${sobrescribirKb ? '#f59e0b' : '#94a3b8'}`,
                  background: sobrescribirKb ? '#f59e0b' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, mt: 0.1,
                }}>
                  {sobrescribirKb && <CheckCircleRoundedIcon sx={{ color: '#fff', fontSize: 12 }}/>}
                </Box>
                <Typography sx={{ fontSize: 11.5, color: sobrescribirKb ? '#92400e' : '#475569', lineHeight: 1.35 }}>
                  Reemplazar base de conocimiento existente
                </Typography>
              </Box>
            </Box>
          </Collapse>
        </DialogContent>
        <Box sx={{ px: 2.5, pb: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button
            onClick={() => setUseDialog(false)} disabled={working}
            sx={{ textTransform: 'none', color: '#64748b', fontWeight: 600 }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={working || (modo === 'nuevo' ? !nombreNuevo.trim() : !chatbotIdExist)}
            onClick={handleCrear}
            sx={{
              textTransform: 'none', fontWeight: 700,
              background: `linear-gradient(135deg, ${selected?.color || '#6366f1'}, ${selected?.color || '#6366f1'}d0)`,
              boxShadow: `0 2px 10px ${(selected?.color || '#6366f1')}40`,
              borderRadius: 2, px: 2.5,
              '&:hover': {
                background: `linear-gradient(135deg, ${selected?.color || '#6366f1'}, ${selected?.color || '#6366f1'})`,
              },
            }}
          >
            {working
              ? <CircularProgress size={16} sx={{ color: '#fff' }}/>
              : (modo === 'nuevo' ? 'Crear chatbot' : 'Aplicar')}
          </Button>
        </Box>
      </Dialog>

      {/* Popover de detalle */}
      <Popover
        open={!!detailAnchor}
        anchorEl={detailAnchor?.el}
        onClose={() => setDetailAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              borderRadius: 2.5,
              border: '1px solid #e2e8f0',
              boxShadow: '0 12px 36px rgba(15, 23, 42, 0.18)',
              maxWidth: 360, width: '92vw',
              overflow: 'hidden',
            },
          },
        }}
      >
        {detailAnchor && (
          <Box>
            {/* Header */}
            <Box sx={{
              px: 2, py: 1.25,
              display: 'flex', alignItems: 'center', gap: 1.25,
              borderBottom: '1px solid #f1f5f9',
            }}>
              {iconOf(detailAnchor.plantilla.icono, detailAnchor.plantilla.color, 28)}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{
                  fontWeight: 700, fontSize: 13, color: '#0f172a',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {detailAnchor.plantilla.nombre}
                </Typography>
                <Typography sx={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>
                  {detailAnchor.plantilla.autor}{detailAnchor.plantilla.especialidad ? ' · ' + detailAnchor.plantilla.especialidad : ''}
                </Typography>
              </Box>
            </Box>

            {/* Cuerpo */}
            <Box sx={{ p: 2, maxHeight: '60vh', overflowY: 'auto' }}>
              {detailLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={20} />
                </Box>
              ) : detailData ? (
                <>
                  {/* Descripción */}
                  <Typography sx={{
                    fontSize: 12.5, color: '#475569', lineHeight: 1.55, mb: 2,
                  }}>
                    {detailData.descripcion || 'Sin descripción.'}
                  </Typography>

                  {/* Intents */}
                  {Array.isArray(detailData.tools_config?.intents) && detailData.tools_config.intents.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <SectionLabel icon={<BoltRoundedIcon sx={{ fontSize: 12 }}/>} color="#6366f1">
                        Intents ({detailData.tools_config.intents.length})
                      </SectionLabel>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {detailData.tools_config.intents.map((it, i) => (
                          <Chip
                            key={i} size="small" label={it.name}
                            sx={{
                              height: 22, fontSize: 11, fontWeight: 600,
                              backgroundColor: '#f1f5f9', color: '#475569',
                              border: '1px solid #e2e8f0',
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Categorías KB */}
                  {Array.isArray(detailData.kb_entries) && detailData.kb_entries.length > 0 && (
                    <Box sx={{ mb: 1.5 }}>
                      <SectionLabel icon={<MenuBookRoundedIcon sx={{ fontSize: 12 }}/>} color="#0ea5e9">
                        Base de conocimiento ({detailData.kb_entries.length})
                      </SectionLabel>
                      {(() => {
                        const byCat = {};
                        detailData.kb_entries.forEach((kb) => {
                          const c = kb.category || 'sin categoría';
                          byCat[c] = (byCat[c] || 0) + 1;
                        });
                        return (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {Object.entries(byCat).map(([cat, n]) => (
                              <Box key={cat} sx={{
                                px: 0.85, py: 0.25, borderRadius: 1,
                                background: '#eff6ff', border: '1px solid #bfdbfe',
                                display: 'flex', alignItems: 'center', gap: 0.4,
                              }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#1e40af', textTransform: 'capitalize' }}>
                                  {cat}
                                </Typography>
                                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#3b82f6' }}>
                                  {n}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        );
                      })()}
                    </Box>
                  )}

                  {/* System prompt preview */}
                  {detailData.system_prompt && (
                    <Box>
                      <SectionLabel icon={<AutoAwesomeRoundedIcon sx={{ fontSize: 12 }}/>} color="#a855f7">
                        Personalidad
                      </SectionLabel>
                      <Box sx={{
                        p: 1.25, borderRadius: 1.5,
                        background: '#fafbfc', border: '1px solid #e2e8f0',
                        fontFamily: '"SF Mono", Menlo, monospace',
                      }}>
                        <Typography sx={{
                          fontSize: 11, color: '#475569', lineHeight: 1.5,
                          overflow: 'hidden', textOverflow: 'ellipsis',
                          display: '-webkit-box', WebkitLineClamp: 6, WebkitBoxOrient: 'vertical',
                          whiteSpace: 'pre-wrap',
                        }}>
                          {detailData.system_prompt.trim().slice(0, 360)}
                          {detailData.system_prompt.length > 360 ? '…' : ''}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </>
              ) : (
                <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>No se pudo cargar el detalle.</Typography>
              )}
            </Box>
          </Box>
        )}
      </Popover>

      <Snackbar
        open={!!snack} autoHideDuration={3500}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack && (
          <Alert severity={snack.severity} variant="filled" sx={{ borderRadius: 2 }}>
            {snack.msg}
          </Alert>
        )}
      </Snackbar>
    </>
  );
}

function SectionLabel({ icon, color, children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.85 }}>
      <Box sx={{ color, display: 'flex' }}>{icon}</Box>
      <Typography sx={{
        fontSize: 10.5, fontWeight: 800, color,
        letterSpacing: 0.4, textTransform: 'uppercase',
      }}>
        {children}
      </Typography>
    </Box>
  );
}

function ModeBtn({ label, icon, active, onClick, color, disabled }) {
  return (
    <Box
      onClick={disabled ? undefined : onClick}
      sx={{
        flex: 1, px: 1, py: 0.85,
        borderRadius: 1.5,
        background: active ? '#fff' : 'transparent',
        boxShadow: active ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        transition: 'all 0.15s',
        color: active ? color : '#64748b',
      }}
    >
      {icon}
      <Typography sx={{
        fontSize: 12, fontWeight: active ? 700 : 600,
        letterSpacing: -0.1,
      }}>
        {label}
      </Typography>
    </Box>
  );
}
